import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { authAPI } from '../api/client'
import { useAuth } from '../contexts/AuthContext'

/**
 * Change password. Also the page a user lands on when their password was set
 * by someone else (installer, administrator) and must be replaced before the
 * panel can be used. A dedicated page (CLAUDE.md §15).
 */
export const ChangePassword: React.FC<{ home: string }> = ({ home }) => {
  const { user, refreshUser, logout } = useAuth()
  const navigate = useNavigate()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const forced = !!user?.must_change_password

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (next !== confirm) {
      setError('The two new passwords do not match')
      return
    }
    setSaving(true)
    try {
      await authAPI.changePassword(current, next)
      await refreshUser()
      navigate(home, { replace: true })
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not change the password')
    } finally {
      setSaving(false)
    }
  }

  const input = 'mt-1 w-full px-3 py-2 border rounded-md dark:bg-gray-900 dark:border-gray-600'
  return (
    <div data-testid="change-password-page" className="min-h-screen flex items-center justify-center p-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 space-y-4"
      >
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Change your password</h1>
        {forced && (
          <p data-testid="change-password-forced" className="text-sm text-gray-600 dark:text-gray-400">
            Your password was set by someone else. Choose your own to continue.
          </p>
        )}
        {error && (
          <div
            data-testid="change-password-error"
            role="alert"
            className="bg-red-50 dark:bg-red-900/30 border border-red-300 text-red-800 dark:text-red-200 rounded p-3"
          >
            {error}
          </div>
        )}
        <div>
          <label htmlFor="cp-current" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Current password
          </label>
          <input id="cp-current" data-testid="change-password-current" type="password" required
                 autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)}
                 className={input} />
        </div>
        <div>
          <label htmlFor="cp-new" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            New password
          </label>
          <input id="cp-new" data-testid="change-password-new" type="password" required minLength={8}
                 autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)}
                 className={input} />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">At least 8 characters.</p>
        </div>
        <div>
          <label htmlFor="cp-confirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            New password again
          </label>
          <input id="cp-confirm" data-testid="change-password-confirm" type="password" required minLength={8}
                 autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                 className={input} />
        </div>
        <button type="submit" disabled={saving} data-testid="change-password-submit"
                className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white rounded-lg">
          {saving ? 'Saving…' : 'Change password'}
        </button>
        {forced && (
          <button type="button" onClick={() => void logout()} className="w-full text-sm text-gray-600 dark:text-gray-400 hover:underline">
            Sign out
          </button>
        )}
      </form>
    </div>
  )
}
